import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { getForgeGuidanceMessage } from '@/services/forgeFailureRouting.ts';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import { useSuiteIdentity } from '@/context/SuiteDraftContext/SuiteDraftContext.ts';
import type {
  ApplyResult,
  CallableContext,
  CompositionRecord,
  PrepareRequest,
  Preview,
  SemanticEdit,
} from '@/types/executionCompositions.ts';
import type {
  BaselineApprovalRequest,
  ReleaseAcceptanceArtifactReviewRequest,
  ReleaseAcceptanceArtifactReviewResponse,
  ReleaseAcceptanceCampaignCaseRunHistoryResponse,
  ReleaseAcceptanceCampaignStatusResponse,
  ReleaseFoundation,
  ReleaseHistoryPage,
  ReleaseReadiness,
} from '@/types/automationReleases.ts';
import type {
  Automations,
  AutomationVersion,
  AutomationVersionsSimple,
} from '@/types/automations.ts';
import { get_automation_versions_url, get_automations_url } from '@/utils/urls.ts';

export function useCompositionScope() {
  const { orgId, projectId } = useOrgProject();
  const identity = useSuiteIdentity();
  return ['execution-compositions', identity, orgId ?? '', projectId ?? ''] as const;
}
const policy = { retry: false, staleTime: 0, refetchOnMount: 'always' as const };

/** Exact metadata lookup covers published and historical versions without scanning feeds. */
export function useCompositionVersion(versionId: string) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQuery({
    queryKey: [...scope, 'version-metadata', versionId],
    enabled: Boolean(versionId && scope[3]),
    ...policy,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<AutomationVersion>(`/automation-versions/${versionId}`, {
        signal,
      });
      if (data.id !== versionId) throw new Error('Version metadata belongs to another target.');
      return data;
    },
  });
}

export function useCompositionImplementations(automationId: string, published: boolean) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useInfiniteQuery({
    queryKey: [...scope, 'implementations', automationId, published],
    enabled: Boolean(automationId && scope[3]),
    ...policy,
    initialPageParam: 1,
    queryFn: async ({ signal, pageParam }) => {
      const { data } = await api.get<AutomationVersionsSimple>(
        get_automation_versions_url(automationId),
        { signal, params: { page: pageParam, published } },
      );
      if (data.next && (!Number.isInteger(data.next.page) || data.next.page <= pageParam))
        throw new Error('Incomplete implementation discovery.');
      return data;
    },
    getNextPageParam: (data) => data.next?.page ?? undefined,
  });
}
export function useCompositionAutomations() {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useInfiniteQuery({
    queryKey: [...scope, 'authoring-automations'],
    enabled: Boolean(scope[3]),
    ...policy,
    initialPageParam: 1,
    queryFn: async ({ signal, pageParam }) => {
      const { data } = await api.get<Automations>(get_automations_url(scope[3]), {
        signal,
        params: { page: pageParam },
      });
      if (data.next && (!Number.isInteger(data.next.page) || data.next.page <= pageParam))
        throw new Error('Incomplete Automation discovery.');
      return data;
    },
    getNextPageParam: (data) => data.next?.page ?? undefined,
  });
}

export function useCallableContext(versionId?: string) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQuery({
    queryKey: [...scope, 'callable', versionId],
    enabled: Boolean(versionId && scope[3]),
    ...policy,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<CallableContext>(
        `/automation-versions/${versionId}/callable-context`,
        { signal },
      );
      if (data.automation_version_id !== versionId || data.project_id !== scope[3])
        throw new Error('Callable source belongs to another target.');
      return data;
    },
  });
}
export function useCompositionRecord(releaseId?: string) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQuery({
    queryKey: [...scope, 'record', releaseId],
    enabled: Boolean(releaseId && scope[3]),
    ...policy,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<CompositionRecord>(`/execution-compositions/${releaseId}`, {
        signal,
      });
      if (
        data.record.release_id !== releaseId ||
        data.candidate.project_id !== scope[3] ||
        data.candidate.schema_version !== 'automation_release_manifest.v3'
      )
        throw new Error('Composition belongs to another target or unsupported schema.');
      return data;
    },
  });
}
export function useCompositionReadiness(releaseId?: string) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQuery({
    queryKey: [...scope, 'readiness', releaseId],
    enabled: Boolean(releaseId && scope[3]),
    ...policy,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<ReleaseReadiness>(
        `/automation-releases/${releaseId}/readiness`,
        { signal },
      );
      if (data.release_id !== releaseId) throw new Error('Evidence belongs to another Release.');
      return data;
    },
  });
}
export function useReleaseAcceptanceCampaignStatus(releaseId?: string) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useQuery({
    queryKey: [...scope, 'acceptance-campaign', releaseId],
    enabled: Boolean(releaseId && scope[3]),
    ...policy,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<ReleaseAcceptanceCampaignStatusResponse>(
        `/automation-releases/${releaseId}/acceptance-campaign/status`,
        { signal },
      );
      if (
        data.release_id !== releaseId ||
        !Array.isArray(data.cases) ||
        !data.cases.every((item) => typeof item.case_id === 'string' && item.case_id.trim())
      )
        throw new Error('Acceptance campaign evidence is malformed.');
      return data;
    },
  });
}
export function useReleaseAcceptanceCampaignCaseHistory(
  releaseId?: string,
  caseId?: string,
  enabled = false,
) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useInfiniteQuery({
    queryKey: [...scope, 'acceptance-campaign-case-history', releaseId, caseId],
    enabled: Boolean(enabled && releaseId && caseId && scope[3]),
    ...policy,
    initialPageParam: 0,
    queryFn: async ({ signal, pageParam }) => {
      const { data } = await api.get<ReleaseAcceptanceCampaignCaseRunHistoryResponse>(
        `/automation-releases/${releaseId}/acceptance-campaign/cases/${caseId}/runs`,
        { signal, params: { offset: pageParam, limit: 25 } },
      );
      if (
        data.release_id !== releaseId ||
        data.case_id !== caseId ||
        !data.runs.every((run) => run.case_id === caseId) ||
        (data.next_offset !== null &&
          (!Number.isInteger(data.next_offset) || data.next_offset <= pageParam))
      )
        throw new Error('Acceptance campaign history belongs to another target.');
      return data;
    },
    getNextPageParam: (data) => data.next_offset ?? undefined,
  });
}
export function useRecordAcceptanceArtifactReview() {
  const api = useApiClient();
  const scope = useCompositionScope();
  const client = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async ({
      releaseId,
      request,
    }: {
      releaseId: string;
      request: ReleaseAcceptanceArtifactReviewRequest;
    }) => {
      const { data } = await api.post<ReleaseAcceptanceArtifactReviewResponse>(
        `/automation-releases/${releaseId}/acceptance-campaign/artifact-reviews`,
        request,
      );
      if (
        data.case_id !== request.case_id ||
        data.deliverable_id !== request.deliverable_id ||
        data.case_run_id !== request.case_run_id ||
        (data.evaluation_id ?? null) !== (request.evaluation_id ?? null) ||
        data.status !== request.status
      )
        throw new Error('Artifact review response belongs to another acceptance deliverable.');
      return data;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: scope }),
  });
}
export function useBaselineApproval() {
  const api = useApiClient();
  const scope = useCompositionScope();
  const client = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async ({
      releaseId,
      request,
    }: {
      releaseId: string;
      request: BaselineApprovalRequest;
    }) => {
      const { data } = await api.post<ReleaseFoundation>(
        `/automation-releases/${releaseId}/baseline-approvals`,
        request,
      );
      if (
        data.id !== releaseId ||
        !Number.isSafeInteger(data.lifecycle_revision) ||
        data.lifecycle_revision <= request.expected_lifecycle_revision
      )
        throw new Error('Baseline approval response belongs to another or unchanged Release.');
      return data;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: scope }),
  });
}
export function useCompositionHistory(suiteId?: string) {
  const api = useApiClient();
  const scope = useCompositionScope();
  return useInfiniteQuery({
    queryKey: [...scope, 'history', suiteId ?? 'standalone'],
    enabled: Boolean(scope[3]),
    ...policy,
    initialPageParam: 0,
    queryFn: async ({ signal, pageParam }) => {
      const { data } = await api.get<ReleaseHistoryPage>(
        `/projects/${scope[3]}/automation-releases`,
        {
          signal,
          params: {
            ...(suiteId ? { suite_id: suiteId } : { standalone: true }),
            offset: pageParam,
            limit: 50,
          },
        },
      );
      if (
        data.next_offset !== null &&
        (!Number.isInteger(data.next_offset) || data.next_offset <= pageParam)
      )
        throw new Error('Invalid Release pagination.');
      return {
        ...data,
        releases: data.releases.filter(
          (item) =>
            item.schema_version === 'automation_release_manifest.v3' &&
            item.suite_id === (suiteId ?? null),
        ),
      };
    },
    getNextPageParam: (data) => data.next_offset ?? undefined,
  });
}
export function useCompositionMutations() {
  const api = useApiClient();
  const scope = useCompositionScope();
  const client = useQueryClient();
  const prepare = useMutation({
    retry: false,
    mutationFn: async (request: PrepareRequest) => {
      if (request.project_id !== scope[3])
        throw new Error('Preparation belongs to another project.');
      const { data } = await api.post<Preview>('/execution-compositions/prepare', request);
      if (
        data.schema_version !== 'execution_composition_preview.v1' ||
        data.intent.request.project_id !== request.project_id ||
        (data.intent.request.suite_id ?? null) !== (request.suite_id ?? null)
      )
        throw new Error('Preview belongs to another target.');
      return data;
    },
  });
  const apply = useMutation({
    retry: false,
    mutationFn: async (request: {
      preview_token: string;
      preview_digest: string;
      request_id: string;
    }) => {
      const { data } = await api.post<ApplyResult>('/execution-compositions/apply', request);
      if (data.schema_version !== 'execution_composition_apply_result.v1' || !data.release_id)
        throw new Error('Could not confirm candidate persistence. Retry the same request.');
      return data;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: scope }),
  });
  const register = useMutation({
    retry: false,
    mutationFn: async (edit: SemanticEdit) => {
      const { data: preview } = await api.post<{
        preview_digest: string;
        apply_eligibility: { eligible: boolean; blocking_reasons: string[] };
        operation_report: { errors: { message: string }[] };
        forge_guidance?: unknown;
      }>('/execution-compositions/semantic-edits/preview', edit);
      if (!preview.apply_eligibility.eligible || !preview.preview_digest)
        throw new Error(
          [
            getForgeGuidanceMessage(preview),
            ...preview.operation_report.errors.map((item) => item.message),
            ...preview.apply_eligibility.blocking_reasons,
          ].filter(Boolean).join('; ') || 'Implementation edit needs repair.',
        );
      const { data } = await api.post<{
        reference: string;
        proposed_edits: {
          source: { automation_id: string; automation_version_id: string };
          candidate: { automation_id: string; automation_version_id: string };
          config_sha256: string;
        }[];
      }>('/execution-compositions/semantic-edits', {
        project_id: scope[3],
        edits: [{ ...edit, expected_preview_digest: preview.preview_digest }],
      });
      return data;
    },
  });
  return { prepare, apply, register };
}
